import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@iwoca/orion';
import { t } from '@lingui/macro';
import clsx from 'clsx';
import { ErrorCard } from 'components/ErrorCard/ErrorCard';
import { LayoutHeadings } from 'components/Layout/InnerLayout/LayoutHeadings/LayoutHeadings';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { Link } from 'react-router-dom';
import { BREAK_S } from 'utils/breakpoints';
import { getEnvironment } from 'utils/environmentHelper/environmentHelper';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { MainList } from './MainList/MainList';
import { SideList } from './SideList/SideList';
import { useWhatsNew } from './useWhatsNew/useWhatsNew';
import styles from './WhatsNew.module.css';
import { WhatsNewLoader } from './WhatsNewLoader/WhatsNewLoader';

type TProps = {
  introducerId: string;
};

export const WhatsNew = ({ introducerId }: TProps) => {
  const listRef = useRef(null);
  const { data, hasData, isLoading, isLoadingMore } = useWhatsNew({
    containerRef: listRef,
    introducerId,
  });
  const [selectedContentId, setSelectedContentId] = useState<string | null>(
    null,
  );
  const [initialized, setInitialized] = useState(false);
  const { width } = useWindowDimensions();
  const isMobile = width <= BREAK_S;

  const handleSelectContent = (contentId: string) => {
    setSelectedContentId(contentId);
  };

  useEffect(() => {
    if (data?.length > 0 && !initialized) {
      setSelectedContentId(data[0]?.id || null);
      setInitialized(true);
    }
  }, [data, initialized]);

  useEffect(() => {
    if (selectedContentId) {
      const content = document.querySelector(`#content-${selectedContentId}`);
      if (content && !isMobile) {
        content.scrollIntoView({ behavior: 'smooth' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContentId]);

  if (isLoading) {
    return <WhatsNewLoader />;
  }

  if (!hasData) {
    return (
      <ErrorCard
        code={500}
        message={t`Sorry, an internal error occurred. Please try again later.`}
        action={<Link to={PATH_NAMES.home()}>{t`Return to dashboard`}</Link>}
      />
    );
  }

  return (
    <div
      className={clsx(styles.whatsNew, {
        [styles.isStaging]: getEnvironment() === 'staging',
      })}
      ref={isMobile ? listRef : undefined}
    >
      <div className={styles.view}>
        <div className={styles.contentWrapper}>
          <LayoutHeadings
            heading={t`What's new`}
            headingAlignment="center"
            subHeading={t`Stay up to date with the latest iwoca news, products, features and more.`}
          />

          <div className={styles.content}>
            {!isMobile && (
              <SideList
                listRef={!isMobile ? listRef : undefined}
                data={data}
                onSelectContent={handleSelectContent}
                selectedContentId={selectedContentId}
              />
            )}
            <MainList data={data} />
          </div>
        </div>
        {isLoadingMore && (
          <div className={styles.spinnerWrapper}>
            <Spinner className={styles.spinner} />
          </div>
        )}
      </div>
    </div>
  );
};
